/**
 * Centralised TanStack Query keys (PHASE1-BUILD-SPEC §7.3).
 *
 * Buckets and accounts each live in a single canonical cache (`['buckets']` /
 * `['accounts']`); vertical hooks read them via `select`. Transaction list keys
 * embed a normalised filters object so equal filters share a cache entry.
 */
import type { Money, MonthStr, TransactionFilters, TransferConfidence } from './types.ts';

/** `GET /api/receipts?status=` filter (used in the `receiptsList` key). */
export type ReceiptStatusFilter = 'unassigned' | 'attached' | 'all';

/** A normalised month range for range-keyed report queries (§6.6): equal ranges
 * share one cache entry because the key hashes to the same `{from,to}`. */
export interface MonthRange {
  from: MonthStr;
  to: MonthStr;
}

/** Query params for `GET /api/transfers/candidates` (used in the key). */
export interface TransferCandidatesQuery {
  window_days?: number;
  min_confidence?: TransferConfidence;
  limit?: number;
}

/** Strip `undefined`/empty values so equal filters produce an equal key. */
export function normalizeFilters(filters: TransactionFilters): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const entries = Object.entries(filters) as [keyof TransactionFilters, unknown][];
  for (const [key, value] of entries) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value) && value.length === 0) continue;
    out[key] = value;
  }
  return out;
}

export const qk = {
  authMe: ['auth', 'me'] as const,
  buckets: ['buckets'] as const,
  accounts: ['accounts'] as const,
  account: (id: number) => ['accounts', id] as const,
  // Nested under the `['accounts']` prefix so any `qk.accounts` invalidation
  // (remove/unhide, merge, …) refreshes the hidden-section list too.
  accountsHidden: ['accounts', 'hidden'] as const,
  rules: ['rules'] as const,
  rule: (id: number) => ['rules', id] as const,
  inbox: ['inbox'] as const,
  // Under the `['inbox']` prefix so the controller's existing once-per-session
  // unmount invalidation (`invalidateQueries({ queryKey: qk.inbox })`) refreshes
  // the detected groups too.
  inboxGroups: ['inbox', 'groups'] as const,
  transactions: ['transactions'] as const,
  transactionsList: (filters: TransactionFilters) =>
    ['transactions', 'list', normalizeFilters(filters)] as const,
  transactionDetail: (id: number) => ['transactions', 'detail', id] as const,
  imports: ['imports'] as const,
  importProfiles: ['imports', 'profiles'] as const,
  // ── Enable Banking (Phase 2, §7.5) ────────────────────────────────────────
  providerCredential: ['provider-credentials'] as const,
  connections: ['connections'] as const,
  connection: (id: number) => ['connections', id] as const,
  aspsps: (country: string) => ['aspsps', country] as const,
  syncRuns: (params: SyncRunsQuery) => ['sync', 'runs', params] as const,
  // ── Budgets & reports (Phase 3, §6.6) ─────────────────────────────────────
  // NOTE: budget month data is CACHED under `reportMonth` (the Budget page
  // reads `GET /api/reports/month`); `['budgets']` is an invalidation-target
  // family only — no query registers under it today, so never target it when
  // you need an actual budget-view refresh.
  budgets: ['budgets'] as const,
  reports: ['reports'] as const,
  reportMonth: (month: MonthStr) => ['reports', 'month', month] as const,
  reportCategories: (month: MonthStr) => ['reports', 'categories', month] as const,
  reportCashflow: (range: MonthRange) => ['reports', 'cashflow', range] as const,
  reportBucketTrend: (range: MonthRange) => ['reports', 'bucket-trend', range] as const,
  reportNetworth: (range: MonthRange) => ['reports', 'networth', range] as const,
  // ── Transfers (Phase 3, §6.6) ─────────────────────────────────────────────
  transferCandidates: (params: TransferCandidatesQuery) =>
    ['transfers', 'candidates', params] as const,
  linkedTransfers: ['transfers', 'linked'] as const,
  // ── FinTS (Phase 4, §7.3) ──────────────────────────────────────────────────
  fintsAvailability: ['fints', 'availability'] as const,
  fintsChallenge: (id: string) => ['fints', 'challenge', id] as const,
  // ── Receipts ("Belege") ────────────────────────────────────────────────────
  receipts: ['receipts'] as const,
  receiptsList: (status: ReceiptStatusFilter) => ['receipts', 'list', status] as const,
  receiptCandidates: (receiptId: number, amount?: Money) =>
    ['receipts', receiptId, 'candidates', amount ?? null] as const,
  receiptSettings: ['receipts', 'settings'] as const,
  // ── Verträge (Phase 6, §4.2/§6.1) ─────────────────────────────────────────
  contracts: ['contracts'] as const,
  contractForecast: (month: MonthStr) => ['contracts', 'forecast', month] as const,
  contractSuggestions: ['contracts', 'suggestions'] as const,
  // ── E-Mail-Import ("Kontowecker", Phase 7, §API) ──────────────────────────
  emailIngestAvailability: ['email-ingest', 'availability'] as const,
  emailConfig: (accountId: number) => ['email-ingest', 'config', accountId] as const,
  emailMessages: (accountId: number, params: EmailMessagesQuery) =>
    ['email-ingest', 'messages', accountId, params] as const,
  emailMessageRaw: (accountId: number, messageId: number) =>
    ['email-ingest', 'messages', accountId, messageId, 'raw'] as const,
} as const;

/** `GET /api/accounts/{id}/email-config/messages` pagination params (used in
 * the `emailMessages` key — equal params share one cache entry). */
export interface EmailMessagesQuery {
  limit: number;
  offset: number;
}

/** Query params for `GET /api/sync/runs` (used in the `syncRuns` key). */
export interface SyncRunsQuery {
  limit?: number;
  connection_id?: number;
}
